"use client";

import {
  useLoadScript,
  GoogleMap,
  MarkerF,
  Marker,
  PolylineF,
} from "@react-google-maps/api";
import { useMemo } from "react";

const MyMap = ({
  center_lat,
  center_lng,
  width,
  height,
  trips,
}: {
  center_lat: number;
  center_lng: number;
  width: number;
  height: number;
  trips: Trip[];
}) => {
  const trips_to_display = trips.filter(
    (trip) =>
      trip.trip_state_category === "APPROACHING" &&
      trip.taxi_data?.taxi_lat &&
      trip.taxi_data?.taxi_lng &&
      trip.taxi_data?.user_pic
  );

  const center = {
    lat: center_lat,
    lng: center_lng,
  };

  const UserMarker: React.FC = () => {
    return <MarkerF position={center} zIndex={-10}></MarkerF>;
  };

  const getPreviousPositions = (trip: Trip) => {
    if (!trip.monitoring_history?.length) return [];
    const previousHistoryToShow = 5;
    const sortedHistory = [...trip.monitoring_history].sort(
      (a, b) => b.taxi_t - a.taxi_t
    ).slice(0, previousHistoryToShow);

    return sortedHistory
      .filter(entry => entry?.taxi_lat && entry?.taxi_lng)
      .map(entry => ({
        lat: entry.taxi_lat!,
        lng: entry.taxi_lng!,
      }));
  };

  const libraries = useMemo(() => ["places"], []);
  const mapCenter = useMemo(() => center, []);
  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: true,
      scrollwheel: false,
    }),
    []
  );
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY as string,
    libraries: libraries as any,
  });

  //render

  if (!trips_to_display.length) {
    return null;
  }

  if (!isLoaded) {
    return <p>Loading...</p>;
  }

  return (
    <GoogleMap
      options={mapOptions}
      zoom={14}
      center={mapCenter}
      mapTypeId={google.maps.MapTypeId.ROADMAP}
      mapContainerStyle={{ width: `${width}px`, height: `${height}px` }}
      onLoad={() => console.log("Map Component Loaded...")}
    >
      <UserMarker />

      {trips_to_display.map((trip) => {
        const currentPosition = {
          lat: trip.taxi_data.taxi_lat!,
          lng: trip.taxi_data.taxi_lng!,
        };
        const previousPositions = getPreviousPositions(trip);

        const completePath = [...previousPositions.reverse(), currentPosition];
        return (
          <div key={`trip-${trip.course_token}`}>
            {previousPositions.length > 0 && (
              <PolylineF
                path={completePath}
                options={{
                  strokeColor: "#4285F4",
                  strokeOpacity: 0.6,
                  strokeWeight: 3,
                  geodesic: true,
                }}
              />
            )}

            {previousPositions.map((position, index) => (
              <Marker
                key={`prev-${trip.course_token}-${index}`}
                position={position}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: "#B8B8B8",
                  fillOpacity: 0.7,
                  strokeColor: "#E0E0E0",
                  strokeWeight: 2,
                }}
                zIndex={5 + index}
              />
            ))}

            {/* Shadow marker for current position */}
            <Marker
              position={currentPosition}
              zIndex={-10}
            />

            {/* Current position marker (driver photo) */}
            <Marker
              icon={{
                url: `${process.env.NEXT_PUBLIC_ASSETS_HOST}${trip.taxi_data.user_pic}`,
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(15, 45),
              }}
              position={currentPosition}
              zIndex={10}
            />
          </div>
        );
      })}
    </GoogleMap>
  );
};

export default MyMap;
